import { httpClient, ApiError } from "@/shared/api/http-client";
import type { Role } from "@/shared/types/role";
import type { AuthRejection } from "@/shared/auth/auth-context";

/**
 * Identity row returned by `GET /api/auth/me`. Hand-written with `readonly`
 * fields rather than imported from the Orval-generated `AuthMeModel`: feature
 * code never imports `src/shared/api/generated/` (CLAUDE.md → "API contract",
 * TD-07); `shared/api/contract/auth.contract.ts` holds this type equal to the
 * model, so a drift fails `npm run typecheck`.
 *
 * `AuthProvider` reads `permissions` (v2.0 A8, additive: absent on an older
 * API, hence optional and re-validated by `parsePermissions`); the remaining
 * fields only need to be structurally faithful, not complete.
 */
export interface BackendUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: Role;
  readonly isActive: boolean;
  readonly createdAt: string;
  /** Sorted role-policy permissions for INTERNAL accounts; `[]` otherwise. */
  readonly permissions?: readonly string[];
}

/**
 * Backend identity endpoint. Returns the caller's user row when it exists,
 * 401/403/404 when the Firebase identity has no corresponding backend account.
 *
 * Hardcoded rather than imported from the Orval-generated `getAuthControllerMeUrl`
 * because the generated module's async functions are unusable (see TD-07) and we
 * only need the path. Keep in sync with the API's `auth.controller.ts` route.
 */
export const AUTH_ME_PATH = "/api/auth/me";

/**
 * Confirm the Firebase-authenticated caller also exists in the backend DB.
 *
 * Resolves with the backend `BackendUser` on a 200. Throws `ApiError` on any
 * non-2xx (401/403/404 = not provisioned, 5xx = backend trouble) and a plain
 * `Error` on network failure — both classified by `classifyVerificationError`.
 *
 * The Firebase Bearer token is attached by `httpClient` from
 * `firebaseAuth.currentUser`, which is set by the time `onIdTokenChanged`
 * fires, so no token needs to be threaded through here.
 */
export function verifyBackendUser(): Promise<BackendUser> {
  return httpClient<BackendUser>(AUTH_ME_PATH);
}

/**
 * Map a verification failure to the reason the UI should show.
 *
 * Only a definitive identity rejection means "this account can't use the
 * backoffice — retrying won't help", so the caller signs the user out:
 *   - 404: the confirmed contract for "Firebase user has no backend row"
 *     (wanderfit-api e2e: "should return 404 for GET /api/auth/me when user
 *     not in DB").
 *   - 401: the Firebase token was rejected even after httpClient's forced
 *     refresh — a persistent auth failure, not a transient one.
 *
 * 403 is deliberately NOT treated as not-provisioned. It's unused on
 * `/api/auth/me` today, and elsewhere in the API 403 carries MFA-required /
 * suspended semantics; mapping it to "contact an administrator" + sign-out
 * would show the wrong message and kick a recoverable user. Until
 * `/api/auth/me` defines a 403 contract (ideally via `body.code`), it falls
 * through to the transient bucket.
 *
 * Everything else (403, 5xx, network, parse, other 4xx) is transient: we still
 * deny access (never let an unverified user in), but the caller keeps the
 * Firebase session so a retry can re-verify without re-authenticating.
 */
export function classifyVerificationError(err: unknown): AuthRejection {
  if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
    return "not-provisioned";
  }
  return "verification-failed";
}
